// api.js - 文字列キーを使用した改良版APIサービス
import axios from 'axios'
import humps from 'humps' // humpsライブラリをインポート
import { useDialogStore } from '@/stores/dialogStore'

// オプションのデフォルト値
const defaultOptions = {
  showErrorDialog: true,
  errorDialogTitle: 'エラー',
  // 認証エラー時のリダイレクト設定
  authRedirect: {
    enabled: true,
  },
  // ケース変換の設定
  caseConversion: {
    enabled: true, // ケース変換機能の有効・無効
    requestToSnakeCase: true, // リクエストデータをスネークケースに変換する設定
    responseToCamelCase: true, // レスポンスデータをキャメルケースに変換する設定
  },
}

// 現在の環境が管理画面かどうかを判定する関数
const isAdminEnvironment = () => {
  // 現在のURL（パス）が/admin/で始まるかどうかで判定
  return window.location.pathname.startsWith('/admin/')
}

// APIクライアントの作成
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL, // 環境変数からAPIのベースURLを取得
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: import.meta.env.VITE_API_MODE === 'local' ? true : false, // Cookieを送信するための設定
})

// カスタムオプションのキー（文字列を使用）
const CUSTOM_OPTIONS = '__custom_options__'

// リクエスト時の共通処理（インターセプター）
apiClient.interceptors.request.use(config => {
  // カスタムオプションを取得
  const customOptions = config[CUSTOM_OPTIONS] || {}

  // ケース変換の設定を取得
  const caseConversionOptions = {
    ...defaultOptions.caseConversion,
    ...customOptions.caseConversion,
  }

  // ケース変換が有効かつスネークケースへの変換が有効な場合
  if (caseConversionOptions.enabled && caseConversionOptions.requestToSnakeCase) {
    // paramsがある場合はスネークケースに変換
    if (config.params) {
      config.params = humps.decamelizeKeys(config.params)
    }

    // データがある場合はスネークケースに変換
    if (config.data) {
      config.data = humps.decamelizeKeys(config.data)
    }
  }

  return config
})

// レスポンス共通処理
apiClient.interceptors.response.use(
  // 成功時の処理
  response => {
    // カスタムオプションを取得
    const customOptions = response.config[CUSTOM_OPTIONS] || {}

    // ケース変換の設定を取得
    const caseConversionOptions = {
      ...defaultOptions.caseConversion,
      ...customOptions.caseConversion,
    }

    // Blobデータの場合はケース変換を行わない
    if (response.config.responseType === 'blob') {
      return response
    }

    // ケース変換が有効かつキャメルケースへの変換が有効な場合
    if (caseConversionOptions.enabled && caseConversionOptions.responseToCamelCase) {
      // レスポンスデータをキャメルケースに変換
      if (response.data) {
        response.data = humps.camelizeKeys(response.data)
      }
    }

    return response
  },
  // エラー時の処理
  async error => {
    // カスタムオプションを取得
    const customOptions = error.config[CUSTOM_OPTIONS] || {}

    // ダイアログ表示の設定を取得（デフォルトはtrue）
    const showErrorDialog = customOptions.showErrorDialog !== undefined ? customOptions.showErrorDialog : defaultOptions.showErrorDialog

    // エラーダイアログのタイトルを取得
    const errorDialogTitle = customOptions.errorDialogTitle || defaultOptions.errorDialogTitle

    // ケース変換の設定を取得
    const caseConversionOptions = {
      ...defaultOptions.caseConversion,
      ...customOptions.caseConversion,
    }

    // エラーレスポンスのデータをキャメルケースに変換（設定が有効な場合）
    if (error.response && error.response.data && caseConversionOptions.enabled && caseConversionOptions.responseToCamelCase) {
      error.response.data = humps.camelizeKeys(error.response.data)
    }

    // 認証エラーチェック
    if (error.response && error.response.data) {
      const { status, data } = error.response
      const errorMessage = data.message || '予期せぬエラーが発生しました。再度お試しください。'

      // 認証エラーのチェック（メッセージに「Unauthenticated」が含まれるか、ステータスが401）
      const isAuthError = typeof errorMessage === 'string' && errorMessage.includes('Unauthenticated')

      // 認証エラー時のリダイレクト設定を取得
      const authRedirect = defaultOptions.authRedirect

      // 認証エラーかつリダイレクトが有効な場合
      if (isAuthError && authRedirect.enabled) {
        // 管理画面かユーザー画面かで適切なリダイレクト先を設定
        const redirectPath = isAdminEnvironment() ? '/admin/login' : '/login'

        // エラーダイアログを表示してからリダイレクト
        if (showErrorDialog) {
          const dialogStore = useDialogStore()
          await dialogStore.showAlertDialog('セッション切れ', '再度ログインしてください。')
        }

        // ログイン画面へリダイレクト（直接locationで移動）
        window.location.href = redirectPath
        return Promise.reject(error)
      }
    }

    // 通常のエラーダイアログ表示処理
    if (showErrorDialog) {
      const dialogStore = useDialogStore()

      // レスポンスがあるエラーの場合
      if (error.response) {
        const { status, data } = error.response

        // エラーメッセージの決定
        const errorMessage = data.message || '予期せぬエラーが発生しました。再度お試しください。'

        // ダイアログ表示して完了を待つ
        await dialogStore.showAlertDialog(errorDialogTitle, errorMessage)
      } else {
        // ネットワークエラーなど、レスポンスが無い場合
        await dialogStore.showAlertDialog(errorDialogTitle, 'ネットワークエラーが発生しました。接続を確認してください。')
      }
    }

    return Promise.reject(error)
  }
)

// API呼び出し用のラッパー関数
export const apiService = {
  // BLOB対応GETリクエスト（他と同じ引数形式）
  async getBlob(url, params = {}, config = {}) {
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config
    const customOptions = { showErrorDialog, errorDialogTitle, caseConversion }
    try {
      const requestConfig = {
        ...axiosConfig,
        params, // 必ずparamsをセット
        responseType: 'blob',
        [CUSTOM_OPTIONS]: customOptions,
      }
      const response = await apiClient.get(url, requestConfig)
      return response
    } catch (error) {
      throw error
    }
  },
  // GETリクエスト
  async get(url, params = {}, config = {}) {
    // カスタムオプションを抽出
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config

    // カスタムオプションをまとめる
    const customOptions = {
      showErrorDialog,
      errorDialogTitle,
      caseConversion,
    }

    try {
      const requestConfig = {
        params,
        ...axiosConfig,
        [CUSTOM_OPTIONS]: customOptions,
      }

      const response = await apiClient.get(url, requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // POSTリクエスト
  async post(url, data = {}, config = {}) {
    // カスタムオプションを抽出
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config

    // カスタムオプションをまとめる
    const customOptions = {
      showErrorDialog,
      errorDialogTitle,
      caseConversion,
    }

    try {
      const requestConfig = {
        ...axiosConfig,
        [CUSTOM_OPTIONS]: customOptions,
      }

      const response = await apiClient.post(url, data, requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // PUTリクエスト
  async put(url, data = {}, config = {}) {
    // カスタムオプションを抽出
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config

    // カスタムオプションをまとめる
    const customOptions = {
      showErrorDialog,
      errorDialogTitle,
      caseConversion,
    }

    try {
      const requestConfig = {
        ...axiosConfig,
        [CUSTOM_OPTIONS]: customOptions,
      }

      const response = await apiClient.put(url, data, requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // DELETEリクエスト
  async delete(url, config = {}) {
    // カスタムオプションを抽出
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config

    // カスタムオプションをまとめる
    const customOptions = {
      showErrorDialog,
      errorDialogTitle,
      caseConversion,
    }

    try {
      const requestConfig = {
        ...axiosConfig,
        [CUSTOM_OPTIONS]: customOptions,
      }

      const response = await apiClient.delete(url, requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // FormDataを使用したPOSTリクエスト
  async postFormData(url, formData, config = {}) {
    // カスタムオプションを抽出
    const { showErrorDialog, errorDialogTitle, caseConversion, ...axiosConfig } = config

    // カスタムオプションをまとめる
    const customOptions = {
      showErrorDialog,
      errorDialogTitle,
      caseConversion: {
        ...defaultOptions.caseConversion,
        // FormDataのリクエストのみケース変換を無効化し、レスポンスは変換を維持
        requestToSnakeCase: false,
        // responseToCamelCaseはデフォルト値(true)を使用
      },
    }

    try {
      const requestConfig = {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
        [CUSTOM_OPTIONS]: customOptions,
      }

      const response = await apiClient.post(url, formData, requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // グローバル設定の更新
  setDefaultOptions(options) {
    Object.assign(defaultOptions, options)
  },
}

export default apiService
